import { Grid, makeStyles } from "@material-ui/core";
import { httpClient } from "api/httpClient";
import { useGridUserDataGlobalFiltersContext } from "contexts/GridUserDataGlobalFiltersProvider";
import { useSocketContext } from "contexts/SocketProvider";
import { useTerminalsContext } from "contexts/TerminalsProvider";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { usePermissions } from "react-admin";
import { useSelector } from "react-redux";
import { getNowByTerminal } from "utils/datetime";
import { isInArray, isNullUndEmpty } from "utils/general";

import { AppManagerRoles } from "../../entities";
import { PortState } from "../../entities/PortDashboardState";
import { useNotifyError } from "../../utils/notifiers/useNotifyError";
import { PortItem } from "./PortItem";

const STATUS_COLOR_MAP = {
	[PortState.BLOCKED]: "#64748B",
	[PortState.FREE]: "#22C55E",
	[PortState.BUSY]: "#EF4444",
};

const getCircleColor = (status) => STATUS_COLOR_MAP[status] || "#64748B";

const useStyles = makeStyles((theme) => ({
	centerSection: {
		flex: "1 1 auto",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		marginLeft: "20px",
	},
	"@keyframes subtlePulse": {
		"0%, 100%": {
			opacity: 1,
		},
		"50%": {
			opacity: 0.7,
		},
	},
}));

export const DashboardPorts = (props) => {
	const classes = useStyles();
	const notifyError = useNotifyError();
	const { permissions } = usePermissions();
	const [ports, setPorts] = React.useState([]);
	const [updatingPortIds, setUpdatingPortIds] = React.useState(new Set());
	const [tryRefresh, setTryRefresh] = React.useState(false);
	const { data: globalFilters = {} } = useGridUserDataGlobalFiltersContext();

	const { data: terminals } = useTerminalsContext();
	const { socket, error: socketError } = useSocketContext();

	const isRefreshing = useRef(false);
	const refreshTimeoutRef = useRef(null);

	const isAppManagerRoles = useMemo(
		() => isInArray(AppManagerRoles, permissions?.role),
		[permissions?.role]
	);
	const terminalId = useMemo(() => {
		const terminalFilterValue = globalFilters?.terminal_id;
		if (!isNullUndEmpty(terminalFilterValue)) {
			return parseInt(terminalFilterValue[0], 10);
		}
		return null;
	}, [globalFilters?.terminal_id]);

	// Memoize the expensive calculation function
	const getUpdatedMinutesOccupied = useCallback(
		(port) => {
			if (!port || !port.port_orders || port.port_orders.length === 0) return "N/A";

			const terminal = terminals?.[port.terminal_id];
			if (!terminal) return "N/A";

			const now = getNowByTerminal(terminal);
			const closest_open_order_section = port.port_orders[0];

			const portOccupiedAtTimeString =
				closest_open_order_section.port_section === "eta"
					? `${closest_open_order_section.order.eta_datetime}`
					: `${closest_open_order_section.order.etd_datetime}`;
			const portOccupiedAt = new Date(portOccupiedAtTimeString);
			const time_remains = portOccupiedAt - now;
			const minutes_remain = Math.ceil(time_remains / 1000 / 60);

			if (minutes_remain === 0) return "< 1";
			if (minutes_remain < 0) return 0;
			return minutes_remain;
		},
		[terminals]
	);

	const getUpdatedMinutesPortStaysBusy = useCallback(
		(port) => {
			if (
				!port ||
				!port.port_orders ||
				port.port_orders.length === 0 ||
				port.state !== PortState.BUSY
			)
				return null;

			const terminal = terminals?.[port.terminal_id];
			if (!terminal) return null;

			const first_blocking_order = port.port_orders.find(
				(orderData) => orderData.order.is_blocking_order
			);
			if (!first_blocking_order) return null;

			const portBecameBusyAtTimeString =
				first_blocking_order.port_section === "etd"
					? `${first_blocking_order.order.etd_datetime}`
					: `${first_blocking_order.order.eta_datetime}`;
			const portBecameBusyAt = new Date(portBecameBusyAtTimeString);
			// calculate time when port must be free again
			const calculatedTimePortWillStayBusy = port.port_taken_duration;
			const portBecomeFreeAt =
				portBecameBusyAt.getTime() + calculatedTimePortWillStayBusy * 60 * 1000;
			const now = getNowByTerminal(terminal);
			const time_remains = portBecomeFreeAt - now;
			const minutes_remain = Math.ceil(time_remains / 1000 / 60);

			return minutes_remain;
		},
		[terminals]
	);

	// Stable reference for API calls
	const fetchPortsDashboardData = useCallback(async () => {
		if (!terminalId) return [];

		try {
			const filterParam = `filter=${encodeURIComponent(
				JSON.stringify({ terminal_id: terminalId })
			)}`;
			const { json } = await httpClient(`dashboard-ports?${filterParam}`);
			return json;
		} catch (error) {
			notifyError(`Error fetching ports dashboard data ${error}`);
			return [];
		}
	}, [terminalId, notifyError]);

	// Stable reference for port updates - maintains original order
	const updatePorts = useCallback((newPorts) => {
		setPorts((prevPorts) => {
			// If no previous ports, this is initial load - use the order from API
			if (prevPorts.length === 0) {
				return newPorts;
			}

			// Create a Map for faster lookups of new port data
			const newPortsMap = new Map(newPorts.map((port) => [port.id, port]));

			// Update existing ports in place, maintaining their exact positions
			const updatedPorts = prevPorts.map((existingPort) => {
				const updatedPortData = newPortsMap.get(existingPort.id);
				if (updatedPortData) {
					// Remove from map since we've processed it
					newPortsMap.delete(existingPort.id);
					// Merge existing port with new data, preserving position
					return { ...existingPort, ...updatedPortData };
				}
				return existingPort;
			});

			// Only add truly new ports (ones that didn't exist before) at the end
			const remainingNewPorts = Array.from(newPortsMap.values());
			return remainingNewPorts.length > 0 ? [...updatedPorts, ...remainingNewPorts] : updatedPorts;
		});
	}, []);

	const updateDashboardData = useCallback(
		(newPorts, isInitialLoad = false) => {
			try {
				const portIds = newPorts.map((port) => port.id);
				setUpdatingPortIds(new Set(portIds));

				if (isInitialLoad) {
					// For initial load, set ports directly to maintain API order
					setPorts(newPorts);
				} else {
					// For updates, use the merge function to maintain positions
					updatePorts(newPorts);
				}

				// Clear updating state after animation
				setTimeout(() => {
					setUpdatingPortIds(new Set());
				}, 800);
			} catch (error) {
				notifyError(`Error updating dashboard data ${error}`);
			}
		},
		[updatePorts, notifyError]
	);

	const refreshDashboardData = useCallback(
		async (forceInitialLoad = false) => {
			if (!terminalId || isRefreshing.current) return;

			isRefreshing.current = true;
			try {
				const data = await fetchPortsDashboardData();
				updateDashboardData(data, forceInitialLoad);
			} finally {
				isRefreshing.current = false;
			}
		},
		[terminalId, fetchPortsDashboardData, updateDashboardData]
	);

	// Debounced refresh with stable reference
	const debouncedRefresh = useCallback(() => {
		if (refreshTimeoutRef.current) {
			clearTimeout(refreshTimeoutRef.current);
		}

		refreshTimeoutRef.current = setTimeout(() => {
			refreshDashboardData();
		}, 100);
	}, [refreshDashboardData]);

	// Memoize processed ports and check for overdue ports
	const processedPorts = useMemo(() => {
		let hasOverduePorts = false;

		const processed = ports
			.filter((port) => port.state !== PortState.BLOCKED)
			.map((port) => {
				const minutes = getUpdatedMinutesOccupied(port);
				// TODO: optimize - don't need to call everytime
				const busyMinutes =
					port.state === PortState.BUSY ? getUpdatedMinutesPortStaysBusy(port) : null;
				// Check if this port is overdue (minutes is 0)
				if (minutes === 0) {
					hasOverduePorts = true;
				}

				return {
					...port,
					color: getCircleColor(port.state),
					minutes,
					port_taken_duration: busyMinutes,
					isUpdating: updatingPortIds.has(port.id),
				};
			});

		// A hidden blocked port gets no socket event when its lock window ends,
		// so the periodic tick has to notice the passed boundary and refetch
		const hasExpiredBlockedPorts = ports.some((port) => {
			if (port.state !== PortState.BLOCKED || !port.lock_datetime_to) {
				return false;
			}
			const terminal = terminals?.[port.terminal_id];
			if (!terminal) {
				return false;
			}
			return new Date(port.lock_datetime_to) <= getNowByTerminal(terminal);
		});

		// If any port is overdue, trigger a refresh
		if (tryRefresh) {
			setTryRefresh(false);
			if (hasOverduePorts || hasExpiredBlockedPorts) {
				debouncedRefresh();
			}
		}

		return processed;
		// Info: no needs  to add tryRefresh to deps as it is changed with ports for refresh
	}, [
		ports,
		terminals,
		updatingPortIds,
		getUpdatedMinutesOccupied,
		getUpdatedMinutesPortStaysBusy,
		debouncedRefresh,
		tryRefresh,
	]);

	useEffect(() => {
		if (socketError) {
			notifyError(socketError);
		}
	}, [socketError, notifyError]);

	useEffect(() => {
		if (!socket || !isAppManagerRoles) return;

		const handlePortsUpdate = (portsJson) => {
			try {
				const data = JSON.parse(portsJson);

				// Only update if the incoming ports are relevant to current terminal filter
				if (terminalId) {
					const relevantPorts = data.filter((port) => port.terminal_id === terminalId);
					if (relevantPorts.length > 0) {
						// Socket updates are never initial loads
						updateDashboardData(relevantPorts, false);
					}
				}
			} catch (error) {
				notifyError(`Error parsing socket data: ${error}`);
			}
		};

		socket.on("update_dashboard_ports", handlePortsUpdate);
		return () => socket.off("update_dashboard_ports", handlePortsUpdate);
	}, [socket, isAppManagerRoles, terminalId, updateDashboardData, notifyError]);

	useEffect(() => {
		setPorts([]);
		if (!terminalId || !isAppManagerRoles) {
			return;
		}

		// This is initial load since we just cleared ports
		refreshDashboardData(true);
	}, [terminalId, isAppManagerRoles, refreshDashboardData]);

	// Periodic updates for time-based calculations
	useEffect(() => {
		if (ports.length === 0) return;

		const interval = setInterval(() => {
			// Force re-calculation of time-dependent values
			setTryRefresh(true);
			setPorts((currentPorts) => [...currentPorts]);
		}, 60 * 1000);

		return () => clearInterval(interval);
	}, [ports.length]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (refreshTimeoutRef.current) {
				clearTimeout(refreshTimeoutRef.current);
			}
		};
	}, []);

	if (!isAppManagerRoles) {
		return null;
	}

	return (
		<div className={classes.centerSection}>
			<Grid
				container
				spacing={1}
				justifyContent="center"
				alignItems="center"
				style={{
					paddingTop: "10px",
					paddingBottom: "10px",
					rowGap: "8px",
				}}
				position="static"
			>
				{processedPorts.map((port) => (
					<PortItem
						key={port.id}
						port={port}
						color={port.color}
						minutes={port.minutes}
						isUpdating={port.isUpdating}
						port_taken_duration={port.port_taken_duration}
					/>
				))}
			</Grid>
		</div>
	);
};
